import React, { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { 
  Card, 
  Row, 
  Col, 
  Button, 
  Alert, 
  Spinner, 
  Badge,
  Container,
  Table,
  Form,
  InputGroup,
  Modal,
  Toast,
  ToastContainer,
  Pagination,
  ProgressBar,
} from "react-bootstrap";
import Header from "../layouts/Header";
import Footer from "../layouts/Footer";
import axios from "axios";
import * as XLSX from 'xlsx';
import {
  ArrowLeft,
  BarChartFill,
  Calendar,
  CheckCircle,
  InfoCircle,
  Person,
  Search,
  PeopleFill,
  Wallet2,
  ArrowRepeat,
  XCircle,
  Clock,
  FileText,
  Award,
  Building,
  Briefcase,
  TrendingUp,
  CurrencyDollar,
  CreditCard,
  FileEarmarkExcel,
  FilePdf,
  Download,
  Phone,
  Envelope,
  Receipt,
} from "react-bootstrap-icons";
import { API_BASE_URL } from "../constants/api_constants";

const MobilisationBonuses = () => {
  const navigate = useNavigate();
  
  // ==================== ÉTATS ====================
  const [loading, setLoading] = useState(true);
  const [bonuses, setBonuses] = useState([]);
  const [stats, setStats] = useState(null);
  const [startDate, setStartDate] = useState(new Date().toISOString().slice(0, 10));
  const [endDate, setEndDate] = useState(new Date().toISOString().slice(0, 10));
  const [searchTerm, setSearchTerm] = useState("");
  const [error, setError] = useState(null);
  const [showPayModal, setShowPayModal] = useState(false);
  const [selectedBonus, setSelectedBonus] = useState(null);
  const [paying, setPaying] = useState(false);
  const [paymentMethod, setPaymentMethod] = useState('bank_transfer');
  const [paymentReference, setPaymentReference] = useState('');
  const [calculating, setCalculating] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [isFirstLoad, setIsFirstLoad] = useState(true);
  
  // États pour les filtres temporaires
  const todayStr = new Date().toISOString().slice(0, 10);
  const [tempStartDate, setTempStartDate] = useState(todayStr);
  const [tempEndDate, setTempEndDate] = useState(todayStr);
  const [tempSearchTerm, setTempSearchTerm] = useState("");
  const [tempItemsPerPage, setTempItemsPerPage] = useState(10);
  const [isFilterApplied, setIsFilterApplied] = useState(false);
  
  const [accountingData, setAccountingData] = useState(null);
  const [showAccountingModal, setShowAccountingModal] = useState(false);
  
  const [mobileNumber, setMobileNumber] = useState('');
  const [mobileOperator, setMobileOperator] = useState('orange');
  const [paymentNotes, setPaymentNotes] = useState('');
  
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [pagination, setPagination] = useState({
    currentPage: 1,
    perPage: 10,
    total: 0,
    lastPage: 1,
    from: 0,
    to: 0
  });
  
  const [isRecalculating, setIsRecalculating] = useState(false);
  const [isDataLoaded, setIsDataLoaded] = useState(false);
  const [hasAutoRegenerated, setHasAutoRegenerated] = useState(false);
  
  const isCalculatingRef = useRef(false);
  const isLoadingRef = useRef(false);
  const searchTimeoutRef = useRef(null);
  const recalculatingTimeoutRef = useRef(null);

  const [showCalculateModal, setShowCalculateModal] = useState(false);
  const [selectedAgent, setSelectedAgent] = useState(null);
  const [agentSearchTerm, setAgentSearchTerm] = useState('');
  const [filteredAgents, setFilteredAgents] = useState([]);
  const [calculatingSingle, setCalculatingSingle] = useState(false);
  const [showResultModal, setShowResultModal] = useState(false);
  const [singleResult, setSingleResult] = useState(null);
  
  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState('');
  const [toastVariant, setToastVariant] = useState('success');
  const [toastTitle, setToastTitle] = useState('');

  // ==================== CONSTANTES & UTILITAIRES ====================
  const defaultCurrency = sessionStorage.getItem("default_currency") || 'XOF';

  const getAuthToken = () => sessionStorage.getItem("authToken");
  
  const getHeaders = () => {
    const token = getAuthToken();
    return {
      'Content-Type': 'application/json',
      'Accept': 'application/json',
      ...(token && { 'Authorization': `Bearer ${token}` })
    };
  };

  const showToastNotification = (title, message, variant = 'success') => {
    setToastTitle(title);
    setToastMessage(message);
    setToastVariant(variant);
    setShowToast(true);
    setTimeout(() => setShowToast(false), 5000);
  };

  const formatCurrency = (amount) => {
    const num = parseFloat(amount) || 0;
    return new Intl.NumberFormat('fr-FR', {
      style: 'currency',
      currency: defaultCurrency,
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(num);
  };

  const formatDate = (dateString) => {
    if (!dateString) return "Date inconnue";
    try {
      let date;
      if (dateString instanceof Date) {
        date = dateString;
      } else if (typeof dateString === 'number') {
        date = new Date(dateString);
      } else {
        const cleanDate = dateString.replace(/-/g, '/');
        date = new Date(cleanDate);
      }
      if (isNaN(date.getTime())) {
        return dateString;
      }
      return date.toLocaleDateString('fr-FR', {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      });
    } catch (e) {
      return dateString;
    }
  };

  const formatPeriod = () => {
    const start = new Date(startDate);
    const end = new Date(endDate);
    return `${start.toLocaleDateString('fr-FR', { day: '2-digit', month: 'long', year: 'numeric' })} - ${end.toLocaleDateString('fr-FR', { day: '2-digit', month: 'long', year: 'numeric' })}`;
  };

  // ==================== VALIDATION DES DATES ====================
  const validateDates = useCallback(() => {
    if (!startDate || !endDate) {
      showToastNotification('⚠️ Attention', 'Veuillez sélectionner une période valide', 'warning');
      return false;
    }
    
    const start = new Date(startDate);
    const end = new Date(endDate);
    
    if (isNaN(start.getTime()) || isNaN(end.getTime())) {
      showToastNotification('⚠️ Erreur', 'Format de date invalide', 'danger');
      return false;
    }
    
    if (start > end) {
      showToastNotification('⚠️ Attention', 'La date de début ne peut pas être après la date de fin', 'warning');
      return false;
    }
    
    return true;
  }, [startDate, endDate]);

  // ==================== GROUPED BONUSES ====================
  const groupedBonuses = useMemo(() => {
    const grouped = {};
    
    bonuses.forEach(bonus => {
      const key = bonus.user_id;
      if (!grouped[key]) {
        grouped[key] = {
          user_id: bonus.user_id,
          agent: bonus.agent,
          total_debit: 0,
          total_credit: 0,
          total_collectes: 0,
          total_contribuables: 0,
          total_taxes: 0,
          fixed_rate_per_collecte: 2,
          bonuses: [],
          has_pending: false,
          isFullyPaid: false
        };
      }
      
      const debit = parseFloat(bonus.total_amount || bonus.amount || 0);
      const credit = parseFloat(bonus.total_paid || 0);
      
      grouped[key].total_debit += debit;
      grouped[key].total_credit += credit;
      grouped[key].total_collectes += parseInt(bonus.collectes_count) || 0;
      grouped[key].total_contribuables += parseInt(bonus.contribuables_count) || 0;
      grouped[key].total_taxes += parseFloat(bonus.taxes_collected) || 0;
      grouped[key].fixed_rate_per_collecte =
        parseFloat(bonus.fixed_rate_per_collecte) || 2;
      grouped[key].bonuses.push(bonus);
      
      if (bonus.status === 'pending') {
        grouped[key].has_pending = true;
      }
    });
    
    Object.values(grouped).forEach(group => {
      const balance = group.total_debit - group.total_credit;
      group.balance = balance;
      group.isSettled = balance <= 0;
      group.isFullyPaid = group.isSettled && group.total_debit > 0;
      group.status = group.isFullyPaid ? 'paid' : 'pending';
      group.hasPendingComplement = balance > 0;
      group.remainingBalance = balance > 0 ? balance : 0;
    });
    
    return Object.values(grouped);
  }, [bonuses]);

  // ==================== COMPUTED STATS ====================
  const computedStats = useMemo(() => {
    if (groupedBonuses.length === 0) {
      return {
        total_agents: 0,
        total_collectes: 0,
        total_contribuables: 0,
        total_taxes_collected: 0,
        total_amount: 0,
        paid_amount: 0,
        pending_amount: 0,
        fixed_rate_per_collecte: 2
      };
    }

    const totalCollectes = groupedBonuses.reduce((sum, g) => sum + g.total_collectes, 0);
    const totalContribuables = groupedBonuses.reduce((sum, g) => sum + g.total_contribuables, 0);
    const totalTaxes = groupedBonuses.reduce((sum, g) => sum + g.total_taxes, 0);
    const totalAmount = groupedBonuses.reduce((sum, g) => sum + g.total_debit, 0);
    const paidAmount = groupedBonuses.filter(g => g.isFullyPaid).reduce((sum, g) => sum + g.total_debit, 0);
    
    return {
      total_agents: groupedBonuses.length,
      total_collectes: totalCollectes,
      total_contribuables: totalContribuables,
      total_taxes_collected: totalTaxes,
      total_amount: totalAmount,
      paid_amount: paidAmount,
      pending_amount: totalAmount - paidAmount,
      fixed_rate_per_collecte: 2
    };
  }, [groupedBonuses]);

  // ==================== COMPUTED ACCOUNTING DATA ====================
  const computedAccountingData = useMemo(() => {
    if (groupedBonuses.length === 0) {
      return {
        date_debut: startDate,
        date_fin: endDate,
        debit: 0,
        credit: 0,
        balance: 0,
        isSettled: true,
        pendingCount: 0,
        paidCount: 0,
        totalBonuses: 0
      };
    }

    const totalDebit = groupedBonuses.reduce((sum, g) => sum + g.total_debit, 0);
    const totalCredit = groupedBonuses.reduce((sum, g) => sum + g.total_credit, 0);
    const balance = totalDebit - totalCredit;
    const paidCount = groupedBonuses.filter(g => g.isFullyPaid).length;
    const pendingCount = groupedBonuses.filter(g => !g.isFullyPaid).length;

    return {
      date_debut: startDate,
      date_fin: endDate,
      debit: totalDebit,
      credit: totalCredit,
      balance: balance,
      isSettled: balance <= 0,
      pendingCount: pendingCount,
      paidCount: paidCount,
      totalBonuses: groupedBonuses.length
    };
  }, [groupedBonuses, startDate, endDate]);

  // ==================== RECALCULER LES PRIMES DE LA PAGE ====================
  const recalculateCurrentPageBonuses = useCallback(async (silent = false) => {
    if (!validateDates()) return false;
    if (isCalculatingRef.current) return false;
    
    isCalculatingRef.current = true;
    setIsRecalculating(true);
    
    try {
      const response = await axios.post(
        `${API_BASE_URL}/agent-finance/recalculate/mobilisation-page`,
        {
          date_debut: startDate,
          date_fin: endDate,
          page: currentPage,
          per_page: itemsPerPage,
          search: searchTerm
        },
        { headers: getHeaders() }
      );
      
      if (response.data.success) {
        const data = response.data.data;
        if (!silent && data.success > 0) {
          const message = `${data.success || 0} agents recalculés, ${data.skipped || 0} déjà payés${data.errors > 0 ? `, ${data.errors} échecs` : ''}`;
          showToastNotification('✅ Recalcul terminé', message, data.errors > 0 ? 'warning' : 'success');
        }
        return true;
      } else {
        throw new Error(response.data.message || 'Erreur lors du recalcul');
      }
    } catch (error) {
      console.error("Erreur recalcul:", error);
      if (!silent) {
        showToastNotification('❌ Erreur', 'Erreur lors du recalcul: ' + (error.response?.data?.message || error.message), 'danger');
      }
      return false;
    } finally {
      isCalculatingRef.current = false;
      setIsRecalculating(false);
    }
  }, [startDate, endDate, currentPage, itemsPerPage, searchTerm, validateDates]);

  // ==================== RÉGÉNÉRATION COMPLÈTE ====================
  const handleFullRegenerate = useCallback(async () => {
    if (isCalculatingRef.current) return;

    try {
      isCalculatingRef.current = true;
      setCalculating(true);
      setIsRecalculating(true);
      setError(null);
      
      const response = await axios.post(
        `${API_BASE_URL}/agent-finance/recalculate/mobilisation-page`,
        {
          date_debut: startDate,
          date_fin: endDate,
          page: 1,
          per_page: 1000,
          search: ''
        },
        { headers: getHeaders() }
      );
      
      if (response.data.success) {
        const data = response.data.data;
        const message = `${data.success || 0} agents régénérés, ${data.skipped || 0} déjà payés${data.errors > 0 ? `, ${data.errors} échecs` : ''}`;
        showToastNotification('✅ Régénération terminée', message, data.errors > 0 ? 'warning' : 'success');
        setHasAutoRegenerated(true);
      } else {
        throw new Error(response.data.message || 'Erreur lors de la régénération');
      }
    } catch (error) {
      console.error("❌ Erreur régénération complète:", error);
      showToastNotification('❌ Erreur', 'Erreur lors de la régénération: ' + (error.response?.data?.message || error.message), 'danger');
    } finally {
      isCalculatingRef.current = false;
      setCalculating(false);
      setIsRecalculating(false);
    }
  }, [startDate, endDate]);

  // ==================== FETCH DATA ====================
  const fetchData = useCallback(async (page = 1, search = searchTerm, skipAutoRegenerate = false, forceRecalculate = false) => {
    if (!validateDates()) return;
    if (isLoadingRef.current) {
      console.log("⏳ Chargement déjà en cours, ignore");
      return;
    }
    
    isLoadingRef.current = true;
    
    try {
      setLoading(true);
      setError(null);
      
      if (forceRecalculate && !skipAutoRegenerate) {
        console.log("🔄 Recalcul forcé des primes pour la période", { startDate, endDate, page, search });
        await recalculateCurrentPageBonuses(true);
      }
      
      const response = await axios.get(
        `${API_BASE_URL}/agent-finance/mobilisation-bonuses-paginated`,
        { 
          headers: getHeaders(),
          params: {
            date_debut: startDate,
            date_fin: endDate,
            page: page,
            per_page: itemsPerPage,
            search: search,
            sort_field: 'taxes_collected',
            sort_direction: 'desc'
          }
        }
      );
      
      const data = response.data.data || response.data;
      const agentsList = data.agents || [];
      const statsData = data.stats || null;
      const paginationData = data.pagination || null;
      
      setPagination({
        currentPage: paginationData?.current_page || 1,
        perPage: paginationData?.per_page || itemsPerPage,
        total: paginationData?.total || 0,
        lastPage: paginationData?.last_page || 1,
        from: paginationData?.from || 0,
        to: paginationData?.to || 0
      });
      
      setCurrentPage(paginationData?.current_page || 1);
      
      const transformedBonuses = [];
      agentsList.forEach(agent => {
        if (agent.bonus) {
          const bonus = agent.bonus;
          transformedBonuses.push({
            id: bonus.id,
            user_id: agent.user_id,
            amount: bonus.amount || 0,
            status: bonus.status || 'pending',
            collectes_count: bonus.collectes_count ?? agent.collectes_count ?? 0,
            contribuables_count: bonus.contribuables_count || 0,
            fixed_rate_per_collecte: bonus.fixed_rate_per_collecte || 2,
            taxes_collected: bonus.taxes_collected || 0,
            payment_date: bonus.payment_date || null,
            period_start: bonus.period_start || startDate,
            period_end: bonus.period_end || endDate,
            bonus_type: bonus.bonus_type || 'mobilisation',
            total_amount: bonus.total_amount || bonus.amount || 0,
            total_paid: bonus.total_paid || 0,
            total_pending: bonus.total_pending || 0,
            has_complement: bonus.has_complement || false,
            is_fully_paid: bonus.is_fully_paid || false,
            is_legacy_paid: bonus.is_legacy_paid || false,
            complements: bonus.complements || [],
            agent: {
              name: agent.nom_complet || `Agent ${agent.user_id}`,
              email: agent.email || 'N/A',
              phone: agent.telephone || 'N/A'
            }
          });
        }
      });
      
      setBonuses(transformedBonuses);
      
      if (statsData) {
        setStats(statsData);
      } else {
        const totalAgents = agentsList.length;
        const totalCollectes = agentsList.reduce((sum, a) => sum + (a.bonus?.collectes_count || 0), 0);
        const totalContribuables = agentsList.reduce((sum, a) => sum + (a.bonus?.contribuables_count || 0), 0);
        const totalTaxes = agentsList.reduce((sum, a) => sum + (a.bonus?.taxes_collected || 0), 0);
        const totalAmount = agentsList.reduce((sum, a) => sum + (a.bonus?.total_amount || 0), 0);
        const paidAmount = agentsList.filter(a => a.bonus?.is_fully_paid).reduce((sum, a) => sum + (a.bonus?.total_amount || 0), 0);
        
        setStats({
          total_agents: totalAgents,
          total_collectes: totalCollectes,
          total_contribuables: totalContribuables,
          total_taxes_collected: totalTaxes,
          total_amount: totalAmount,
          paid_amount: paidAmount,
          pending_amount: totalAmount - paidAmount,
          fixed_rate_per_collecte: 2
        });
      }
      
      setIsDataLoaded(true);
      
      if (!skipAutoRegenerate && !hasAutoRegenerated && agentsList.length > 0) {
        console.log("🔄 Régénération automatique au premier chargement");
        await handleFullRegenerate();
        setHasAutoRegenerated(true);
        
        const refreshResponse = await axios.get(
          `${API_BASE_URL}/agent-finance/mobilisation-bonuses-paginated`,
          { 
            headers: getHeaders(),
            params: {
              date_debut: startDate,
              date_fin: endDate,
              page: page,
              per_page: itemsPerPage,
              search: search,
              sort_field: 'taxes_collected',
              sort_direction: 'desc'
            }
          }
        );
        const refreshData2 = refreshResponse.data.data || refreshResponse.data;
        const refreshAgents = refreshData2.agents || [];
        const refreshPagination = refreshData2.pagination || null;
        
        setPagination({
          currentPage: refreshPagination?.current_page || 1,
          perPage: refreshPagination?.per_page || itemsPerPage,
          total: refreshPagination?.total || 0,
          lastPage: refreshPagination?.last_page || 1,
          from: refreshPagination?.from || 0,
          to: refreshPagination?.to || 0
        });
        
        const refreshBonuses = [];
        refreshAgents.forEach(agent => {
          if (agent.bonus) {
            const bonus = agent.bonus;
            refreshBonuses.push({
              id: bonus.id,
              user_id: agent.user_id,
              amount: bonus.amount || 0,
              status: bonus.status || 'pending',
              collectes_count: bonus.collectes_count ?? agent.collectes_count ?? 0,
              contribuables_count: bonus.contribuables_count || 0,
              fixed_rate_per_collecte: bonus.fixed_rate_per_collecte || 2,
              taxes_collected: bonus.taxes_collected || 0,
              payment_date: bonus.payment_date || null,
              period_start: bonus.period_start || startDate,
              period_end: bonus.period_end || endDate,
              bonus_type: bonus.bonus_type || 'mobilisation',
              total_amount: bonus.total_amount || bonus.amount || 0,
              total_paid: bonus.total_paid || 0,
              total_pending: bonus.total_pending || 0,
              has_complement: bonus.has_complement || false,
              is_fully_paid: bonus.is_fully_paid || false,
              is_legacy_paid: bonus.is_legacy_paid || false,
              complements: bonus.complements || [],
              agent: {
                name: agent.nom_complet || `Agent ${agent.user_id}`,
                email: agent.email || 'N/A',
                phone: agent.telephone || 'N/A'
              }
            });
          }
        });
        setBonuses(refreshBonuses);
        
        if (refreshData2.stats) {
          setStats(refreshData2.stats);
        }
      }
      
    } catch (err) {
      console.error("Erreur chargement:", err);
      setError(err.response?.data?.message || err.message || "Erreur lors du chargement");
      setBonuses([]);
      setPagination({
        currentPage: 1,
        perPage: itemsPerPage,
        total: 0,
        lastPage: 1,
        from: 0,
        to: 0
      });
    } finally {
      setLoading(false);
      isLoadingRef.current = false;
    }
  }, [startDate, endDate, itemsPerPage, searchTerm, hasAutoRegenerated, validateDates, recalculateCurrentPageBonuses, handleFullRegenerate]);

  // ==================== REFRESH DATA ====================
  const refreshData = useCallback(async (showLoading = true, forceRecalculate = false) => {
    await fetchData(currentPage, searchTerm, true, forceRecalculate);
  }, [fetchData, currentPage, searchTerm]);

  // ==================== REGENERATE ALL BONUSES ====================
  const handleRegenerateBonuses = useCallback(async () => {
    await handleFullRegenerate();
    await fetchData(1, searchTerm, true, false);
  }, [handleFullRegenerate, fetchData, searchTerm]);

  // ==================== REGENERATE FIRST TEN ====================
  const handleRegenerateFirstTen = useCallback(async () => {
    await recalculateCurrentPageBonuses(false);
    await fetchData(currentPage, searchTerm, true, false);
  }, [recalculateCurrentPageBonuses, fetchData, currentPage, searchTerm]);

  // ==================== FETCH DIRECT ====================
  const fetchDirect = useCallback(async (page, search, startDateParam, endDateParam, perPageParam, skipAutoRegenerate = false, forceRecalculate = false) => {
    if (!startDateParam || !endDateParam) {
      showToastNotification('⚠️ Attention', 'Veuillez sélectionner une période valide', 'warning');
      return;
    }
    
    if (isLoadingRef.current) {
      console.log("⏳ Chargement déjà en cours, ignore");
      return;
    }
    
    isLoadingRef.current = true;
    
    try {
      setLoading(true);
      setError(null);
      
      if (forceRecalculate && !skipAutoRegenerate) {
        console.log("🔄 Recalcul forcé des primes pour la période", { startDate: startDateParam, endDate: endDateParam, page, search });
        const recalcResponse = await axios.post(
          `${API_BASE_URL}/agent-finance/recalculate/mobilisation-page`,
          {
            date_debut: startDateParam,
            date_fin: endDateParam,
            page: page,
            per_page: perPageParam || itemsPerPage,
            search: search
          },
          { headers: getHeaders() }
        );
        console.log("✅ Recalcul terminé:", recalcResponse.data);
      }
      
      const response = await axios.get(
        `${API_BASE_URL}/agent-finance/mobilisation-bonuses-paginated`,
        { 
          headers: getHeaders(),
          params: {
            date_debut: startDateParam,
            date_fin: endDateParam,
            page: page,
            per_page: perPageParam || itemsPerPage,
            search: search,
            sort_field: 'taxes_collected',
            sort_direction: 'desc'
          }
        }
      );
      
      const data = response.data.data || response.data;
      const agentsList = data.agents || [];
      const statsData = data.stats || null;
      const paginationData = data.pagination || null;
      
      setPagination({
        currentPage: paginationData?.current_page || 1,
        perPage: paginationData?.per_page || (perPageParam || itemsPerPage),
        total: paginationData?.total || 0,
        lastPage: paginationData?.last_page || 1,
        from: paginationData?.from || 0,
        to: paginationData?.to || 0
      });
      
      setCurrentPage(paginationData?.current_page || 1);
      
      const transformedBonuses = [];
      agentsList.forEach(agent => {
        if (agent.bonus) {
          const bonus = agent.bonus;
          transformedBonuses.push({
            id: bonus.id,
            user_id: agent.user_id,
            amount: bonus.amount || 0,
            status: bonus.status || 'pending',
            collectes_count: bonus.collectes_count ?? agent.collectes_count ?? 0,
            contribuables_count: bonus.contribuables_count || 0,
            fixed_rate_per_collecte: bonus.fixed_rate_per_collecte || 2,
            taxes_collected: bonus.taxes_collected || 0,
            payment_date: bonus.payment_date || null,
            period_start: bonus.period_start || startDateParam,
            period_end: bonus.period_end || endDateParam,
            bonus_type: bonus.bonus_type || 'mobilisation',
            total_amount: bonus.total_amount || bonus.amount || 0,
            total_paid: bonus.total_paid || 0,
            total_pending: bonus.total_pending || 0,
            has_complement: bonus.has_complement || false,
            is_fully_paid: bonus.is_fully_paid || false,
            is_legacy_paid: bonus.is_legacy_paid || false,
            complements: bonus.complements || [],
            agent: {
              name: agent.nom_complet || `Agent ${agent.user_id}`,
              email: agent.email || 'N/A',
              phone: agent.telephone || 'N/A'
            }
          });
        }
      });
      
      setBonuses(transformedBonuses);
      
      if (statsData) {
        setStats(statsData);
      } else {
        const totalAgents = agentsList.length;
        const totalCollectes = agentsList.reduce((sum, a) => sum + (a.bonus?.collectes_count || 0), 0);
        const totalContribuables = agentsList.reduce((sum, a) => sum + (a.bonus?.contribuables_count || 0), 0);
        const totalTaxes = agentsList.reduce((sum, a) => sum + (a.bonus?.taxes_collected || 0), 0);
        const totalAmount = agentsList.reduce((sum, a) => sum + (a.bonus?.total_amount || 0), 0);
        const paidAmount = agentsList.filter(a => a.bonus?.is_fully_paid).reduce((sum, a) => sum + (a.bonus?.total_amount || 0), 0);
        
        setStats({
          total_agents: totalAgents,
          total_collectes: totalCollectes,
          total_contribuables: totalContribuables,
          total_taxes_collected: totalTaxes,
          total_amount: totalAmount,
          paid_amount: paidAmount,
          pending_amount: totalAmount - paidAmount,
          fixed_rate_per_collecte: 2
        });
      }
      
      setIsDataLoaded(true);
      
      if (!skipAutoRegenerate && !hasAutoRegenerated && agentsList.length > 0) {
        console.log("🔄 Régénération automatique au premier chargement");
        await handleFullRegenerate();
        setHasAutoRegenerated(true);
      }
      
    } catch (err) {
      console.error("Erreur chargement:", err);
      setError(err.response?.data?.message || err.message || "Erreur lors du chargement");
      setBonuses([]);
      setPagination({
        currentPage: 1,
        perPage: perPageParam || itemsPerPage,
        total: 0,
        lastPage: 1,
        from: 0,
        to: 0
      });
    } finally {
      setLoading(false);
      isLoadingRef.current = false;
    }
  }, [itemsPerPage, hasAutoRegenerated, handleFullRegenerate]);

  // ==================== APPLIQUER LES FILTRES ====================
  const handleApplyFilters = useCallback(async () => {
    if (!tempStartDate || !tempEndDate) {
      showToastNotification('⚠️ Attention', 'Veuillez sélectionner une période valide', 'warning');
      return;
    }
    
    const start = new Date(tempStartDate);
    const end = new Date(tempEndDate);
    
    if (isNaN(start.getTime()) || isNaN(end.getTime())) {
      showToastNotification('⚠️ Erreur', 'Format de date invalide', 'danger');
      return;
    }
    
    if (start > end) {
      showToastNotification('⚠️ Attention', 'La date de début ne peut pas être après la date de fin', 'warning');
      return;
    }
    
    setStartDate(tempStartDate);
    setEndDate(tempEndDate);
    setSearchTerm(tempSearchTerm);
    setItemsPerPage(tempItemsPerPage);
    setCurrentPage(1);
    setHasAutoRegenerated(false);
    setIsFilterApplied(true);
    
    showToastNotification('✅ Filtres appliqués', 'Chargement des données avec les nouveaux filtres...', 'info');
    
    await fetchDirect(1, tempSearchTerm, tempStartDate, tempEndDate, tempItemsPerPage, true, true);
    
    showToastNotification('✅ Succès', 'Les filtres ont été appliqués avec succès', 'success');
  }, [tempStartDate, tempEndDate, tempSearchTerm, tempItemsPerPage, fetchDirect]);

  // ==================== RÉINITIALISER LES FILTRES ====================
  const handleResetFilters = useCallback(() => {
    const today = new Date().toISOString().slice(0, 10);
    
    setTempStartDate(today);
    setTempEndDate(today);
    setTempSearchTerm("");
    setTempItemsPerPage(10);
    
    setStartDate(today);
    setEndDate(today);
    setSearchTerm("");
    setItemsPerPage(10);
    setCurrentPage(1);
    setHasAutoRegenerated(false);
    setIsFilterApplied(false);
    
    showToastNotification('🔄 Réinitialisation', 'Réinitialisation des filtres en cours...', 'info');
    
    fetchDirect(1, "", today, today, 10, true, true);
    
    showToastNotification('🔄 Filtres réinitialisés', 'Les filtres ont été réinitialisés', 'info');
  }, [fetchDirect]);

  // ==================== CALCUL POUR UN AGENT ====================
  const handleCalculateSingleAgent = async () => {
    if (!selectedAgent) {
      showToastNotification('⚠️ Attention', 'Veuillez sélectionner un agent', 'warning');
      return;
    }

    setCalculatingSingle(true);
    
    try {
      const response = await axios.post(
        `${API_BASE_URL}/agent-finance/calculate/mobilisation`,
        {
          agent_id: selectedAgent.user_id,
          date_debut: startDate,
          date_fin: endDate
        },
        { headers: getHeaders() }
      );
      
      if (response.data.success) {
        const data = response.data.data;
        
        const globalStatus = data.global_status || data.status || 'pending';
        const isFullyPaid = globalStatus === 'paid' || data.is_fully_paid === true;
        
        setSingleResult({
          agent_id: selectedAgent.user_id,
          agent_name: selectedAgent.nom_complet || data.agent_name || 'Agent',
          taxes_collected: data.taxes_collected || 0,
          collectes_count: data.collectes_count || 0,
          contribuables_count: data.contribuables_count || 0,
          total_amount: data.total_amount || 0,
          total_paid: data.total_paid || 0,
          total_pending: data.total_pending || data.total_amount || 0,
          bonus_id: data.bonus_id || null,
          status: data.status || 'pending',
          global_status: globalStatus,
          is_fully_paid: isFullyPaid,
          fixed_rate_per_collecte: data.fixed_rate_per_collecte || 2,
          period_start: startDate,
          period_end: endDate,
          has_complement: data.has_complement || false,
          complements: data.complements || []
        });
        
        setShowResultModal(true);
        setShowCalculateModal(false);
        setSelectedAgent(null);
        setAgentSearchTerm('');
        await refreshData(true, false);
        showToastNotification('✅ Calcul terminé', `Prime calculée pour ${selectedAgent.nom_complet}`, 'success');
      } else {
        showToastNotification('❌ Erreur', response.data.message || 'Erreur lors du calcul', 'danger');
      }
    } catch (error) {
      console.error("Erreur calcul agent:", error);
      showToastNotification('❌ Erreur', error.response?.data?.message || error.message || 'Erreur lors du calcul', 'danger');
    } finally {
      setCalculatingSingle(false);
    }
  };

  // ==================== OPEN CALCULATE MODAL ====================
  const openCalculateModal = () => {
    const allAgents = [];
    
    if (groupedBonuses && groupedBonuses.length > 0) {
      groupedBonuses.forEach(group => {
        const totalDebit = parseFloat(group.total_debit || 0);
        const totalCredit = parseFloat(group.total_credit || 0);
        const balance = totalDebit - totalCredit;
        
        let status = 'pending';
        if (totalDebit > 0 && balance <= 0) {
          status = 'paid';
        } else if (totalDebit === 0) {
          status = 'not_calculated';
        }
        
        allAgents.push({
          user_id: group.user_id,
          nom_complet: group.agent?.name || `Agent ${group.user_id}`,
          email: group.agent?.email || 'N/A',
          telephone: group.agent?.phone || 'N/A',
          has_bonus: totalDebit > 0,
          collectes_count: group.total_collectes || 0,
          total_amount: totalDebit,
          total_paid: totalCredit,
          balance: balance,
          status: status,
          is_fully_paid: status === 'paid'
        });
      });
    }
    
    setFilteredAgents(allAgents);
    setAgentSearchTerm('');
    setSelectedAgent(null);
    setShowCalculateModal(true);
  };

  // ==================== PAIEMENT ====================
  const handlePayBonus = async () => {
    try {
      setPaying(true);
      
      if (!selectedBonus) {
        showToastNotification('⚠️ Erreur', 'Aucune prime sélectionnée', 'danger');
        setPaying(false);
        return;
      }

      const amountToPay = selectedBonus.remaining_balance || selectedBonus.amount || 0;
      
      if (amountToPay <= 0) {
        showToastNotification('⚠️ Attention', 'Le montant à payer est nul', 'warning');
        setPaying(false);
        return;
      }

      const paymentData = {
        bonus_ids: [selectedBonus.id],
        payment_method: paymentMethod,
        reference: paymentReference || `PAY-${Date.now()}`,
        pay_balance: true
      };

      if (paymentMethod === 'mobile_money' || paymentMethod === 'orange_money' || paymentMethod === 'mtn_money' || paymentMethod === 'wave') {
        paymentData.mobile_number = mobileNumber;
        paymentData.mobile_operator = mobileOperator;
      }

      if (paymentNotes) {
        paymentData.notes = paymentNotes;
      }

      const response = await axios.post(
        `${API_BASE_URL}/agent-finance/pay`,
        paymentData,
        { headers: getHeaders() }
      );

      if (response.data.success) {
        showToastNotification('✅ Paiement effectué', `Paiement de ${formatCurrency(amountToPay)} effectué`, 'success');
        setShowPayModal(false);
        setPaymentReference('');
        setMobileNumber('');
        setMobileOperator('orange');
        setPaymentNotes('');
        await refreshData(true, true);
      } else {
        showToastNotification('❌ Erreur', response.data.message || "Erreur lors du paiement", 'danger');
      }
    } catch (error) {
      console.error("❌ Erreur paiement:", error);
      showToastNotification('❌ Erreur', error.response?.data?.message || error.message || "Erreur lors du paiement", 'danger');
    } finally {
      setPaying(false);
    }
  };

  // ==================== EXPORT EXCEL ====================
  const handleExportExcel = async () => {
    try {
      setExporting(true);
      
      let allAgents = [];
      let currentPageApi = 1;
      let hasMore = true;
      
      while (hasMore) {
        const response = await axios.get(
          `${API_BASE_URL}/agent-finance/mobilisation-bonuses-paginated`,
          {
            headers: getHeaders(),
            params: {
              date_debut: startDate,
              date_fin: endDate,
              page: currentPageApi,
              per_page: 100,
              search: ''
            }
          }
        );
        
        const data = response.data.data || response.data;
        const agentsList = data.agents || [];
        const paginationData = data.pagination || {};
        
        allAgents = [...allAgents, ...agentsList];
        
        hasMore = currentPageApi < paginationData.last_page;
        currentPageApi++;
      }
      
      const allData = allAgents.map(agent => {
        const bonus = agent.bonus;
        const debit = bonus ? parseFloat(bonus.total_amount || 0) : 0;
        const credit = bonus ? parseFloat(bonus.total_paid || 0) : 0;
        const balance = debit - credit;
        
        return {
          'Agent': agent.nom_complet || 'N/A',
          'Email': agent.email || 'N/A',
          'Téléphone': agent.telephone || 'N/A',
          'Collectes': bonus?.collectes_count || 0,
          'Contribuables': bonus?.contribuables_count || 0,
          'Taxes collectées': formatCurrency(bonus?.taxes_collected || 0),
          'Taux ($/collecte)': bonus?.fixed_rate_per_collecte || 2,
          'DÉBIT (Prime)': formatCurrency(debit),
          'CRÉDIT (Payé)': formatCurrency(credit),
          'Solde': formatCurrency(Math.abs(balance)),
          'Statut': bonus?.is_fully_paid ? 'Payée' : 'En attente',
          'Période': `${formatDate(startDate)} - ${formatDate(endDate)}`
        };
      });

      const totalDebit = allAgents.reduce((sum, a) => sum + (a.bonus?.total_amount || 0), 0);
      const totalCredit = allAgents.reduce((sum, a) => sum + (a.bonus?.total_paid || 0), 0);
      const totalBalance = totalDebit - totalCredit;
      const paidCount = allAgents.filter(a => a.bonus?.is_fully_paid).length;
      const totalCollectes = allAgents.reduce((sum, a) => sum + (a.bonus?.collectes_count || 0), 0);

      if (allData.length > 0) {
        allData.push({
          'Agent': '--- SYNTHÈSE COMPTABLE ---',
          'Email': '',
          'Téléphone': '',
          'Collectes': totalCollectes,
          'Contribuables': '',
          'Taxes collectées': '',
          'Taux ($/collecte)': '',
          'DÉBIT (Prime)': '',
          'CRÉDIT (Payé)': '',
          'Solde': '',
          'Statut': '',
          'Période': ''
        });
        allData.push({
          'Agent': 'TOTAL DÉBIT',
          'Email': '',
          'Téléphone': '',
          'Collectes': '',
          'Contribuables': '',
          'Taxes collectées': '',
          'Taux ($/collecte)': '',
          'DÉBIT (Prime)': formatCurrency(totalDebit),
          'CRÉDIT (Payé)': '',
          'Solde': '',
          'Statut': '',
          'Période': ''
        });
        allData.push({
          'Agent': 'TOTAL CRÉDIT',
          'Email': '',
          'Téléphone': '',
          'Collectes': '',
          'Contribuables': '',
          'Taxes collectées': '',
          'Taux ($/collecte)': '',
          'DÉBIT (Prime)': '',
          'CRÉDIT (Payé)': formatCurrency(totalCredit),
          'Solde': '',
          'Statut': '',
          'Période': ''
        });
        allData.push({
          'Agent': `SOLDE (${totalBalance <= 0 ? 'SOLDÉ' : 'NON SOLDÉ'})`,
          'Email': '',
          'Téléphone': '',
          'Collectes': '',
          'Contribuables': '',
          'Taxes collectées': '',
          'Taux ($/collecte)': '',
          'DÉBIT (Prime)': '',
          'CRÉDIT (Payé)': '',
          'Solde': formatCurrency(Math.abs(totalBalance)),
          'Statut': totalBalance <= 0 ? 'Soldé' : 'Non soldé',
          'Période': ''
        });
        allData.push({
          'Agent': `STATUT: ${paidCount}/${allAgents.length} primes payées`,
          'Email': '',
          'Téléphone': '',
          'Collectes': '',
          'Contribuables': '',
          'Taxes collectées': '',
          'Taux ($/collecte)': '',
          'DÉBIT (Prime)': '',
          'CRÉDIT (Payé)': '',
          'Solde': '',
          'Statut': '',
          'Période': ''
        });
      }

      const wb = XLSX.utils.book_new();
      const ws = XLSX.utils.json_to_sheet(allData);
      
      ws['!cols'] = [
        { wch: 30 }, { wch: 25 }, { wch: 15 }, { wch: 12 },
        { wch: 15 }, { wch: 15 }, { wch: 12 }, { wch: 15 },
        { wch: 15 }, { wch: 15 }, { wch: 15 }, { wch: 25 }
      ];

      XLSX.utils.book_append_sheet(wb, ws, 'Primes mobilisation');
      
      const fileName = `primes_mobilisation_${new Date().toISOString().split('T')[0]}.xlsx`;
      XLSX.writeFile(wb, fileName);
      
      showToastNotification('✅ Succès', `Export Excel terminé: ${allAgents.length} agents exportés`, 'success');
    } catch (error) {
      console.error("Erreur export Excel:", error);
      showToastNotification('❌ Erreur', 'Erreur lors de l\'export Excel: ' + error.message, 'danger');
    } finally {
      setExporting(false);
    }
  };

  // ==================== EXPORT PDF ====================
  const handleExportPDF = async () => {
    if (exporting) return;
    setExporting(true);
    
    try {
      let allAgents = [];
      let currentPageApi = 1;
      let hasMore = true;
      
      while (hasMore) {
        const response = await axios.get(
          `${API_BASE_URL}/agent-finance/mobilisation-bonuses-paginated`,
          {
            headers: getHeaders(),
            params: {
              date_debut: startDate,
              date_fin: endDate,
              page: currentPageApi,
              per_page: 100,
              search: ''
            }
          }
        );
        
        const data = response.data.data || response.data;
        const agentsList = data.agents || [];
        const paginationData = data.pagination || {};
        
        allAgents = [...allAgents, ...agentsList];
        
        hasMore = currentPageApi < paginationData.last_page;
        currentPageApi++;
      }
      
      const fileName = `PRIMES_MOBILISATION_${new Date().toISOString().split('T')[0]}`;
      const originalTitle = document.title;
      document.title = fileName;

      const formatCurrencyPDF = (amount) => {
        const num = parseFloat(amount) || 0;
        return num.toLocaleString('fr-FR', { minimumFractionDigits: 0, maximumFractionDigits: 0 }) + ' ' + defaultCurrency;
      };

      const totalDebit = allAgents.reduce((sum, a) => sum + (a.bonus?.total_amount || 0), 0);
      const totalCredit = allAgents.reduce((sum, a) => sum + (a.bonus?.total_paid || 0), 0);
      const totalBalance = totalDebit - totalCredit;
      const paidCount = allAgents.filter(a => a.bonus?.is_fully_paid).length;
      const totalCollectes = allAgents.reduce((sum, a) => sum + (a.bonus?.collectes_count || 0), 0);
      const totalContribuables = allAgents.reduce((sum, a) => sum + (a.bonus?.contribuables_count || 0), 0);
      const totalTaxes = allAgents.reduce((sum, a) => sum + (a.bonus?.taxes_collected || 0), 0);

      const htmlContent = `<!DOCTYPE html>
<html lang="fr">
<head>
  <meta charset="UTF-8">
  <title>${fileName}</title>
  <style>
    * { margin: 0; padding: 0; box-sizing: border-box; }
    body { font-family: Arial, Helvetica, sans-serif; padding: 4mm 5mm; background: white; font-size: 9pt; line-height: 1.25; color: #1a1a1a; }
    .report { max-width: 100%; margin: 0 auto; }
    .header { text-align: center; padding-bottom: 8px; border-bottom: 3px solid #1a3a6a; margin-bottom: 12px; }
    .header h1 { font-size: 16pt; font-weight: bold; text-transform: uppercase; color: #1a3a6a; }
    .header .subtitle { font-size: 10pt; color: #444; }
    .header .meta { font-size: 8pt; color: #555; margin-top: 4px; }
    .section { margin-bottom: 10px; }
    .section-title { font-size: 10pt; font-weight: bold; text-transform: uppercase; border-bottom: 2px solid #999; padding-bottom: 3px; margin-bottom: 6px; color: #1a3a6a; }
    .stats-grid { display: grid; grid-template-columns: repeat(4, 1fr); gap: 8px; margin-bottom: 10px; }
    .stat-item { background: #f8f9fa; padding: 8px 12px; border-radius: 4px; text-align: center; }
    .stat-item .label { font-size: 7pt; color: #666; text-transform: uppercase; }
    .stat-item .value { font-size: 11pt; font-weight: bold; }
    .stat-item .value.primary { color: #1a3a6a; }
    .stat-item .value.success { color: #28a745; }
    .stat-item .value.warning { color: #ffc107; }
    .stat-item .value.danger { color: #dc3545; }
    table { width: 100%; border-collapse: collapse; font-size: 8pt; margin-top: 4px; }
    table thead th { background: #1a3a6a; color: white; padding: 4px 6px; text-align: left; font-weight: bold; border: 1px solid #1a3a6a; text-transform: uppercase; }
    table tbody td { padding: 3px 6px; border: 1px solid #ddd; }
    table tbody tr:nth-child(even) { background: #f8f9fa; }
    .text-right { text-align: right; }
    .text-center { text-align: center; }
    .fw-bold { font-weight: bold; }
    .badge { display: inline-block; padding: 1px 8px; border-radius: 3px; font-size: 7.5pt; font-weight: bold; }
    .badge.paid { background: #d4edda; color: #155724; }
    .badge.pending { background: #fff3cd; color: #856404; }
    .accounting-summary { background: #f8f9fa; border: 1px solid #dee2e6; border-radius: 4px; padding: 8px 12px; margin-bottom: 10px; }
    .accounting-summary .row { display: flex; justify-content: space-around; }
    .accounting-summary .col { text-align: center; }
    .accounting-summary .label { font-size: 7pt; color: #666; text-transform: uppercase; }
    .accounting-summary .value { font-size: 11pt; font-weight: bold; }
    .accounting-summary .value.debit { color: #dc3545; }
    .accounting-summary .value.credit { color: #28a745; }
    .accounting-summary .value.balance { color: #ffc107; }
    .footer { margin-top: 15px; padding-top: 6px; border-top: 1px solid #ccc; font-size: 7.5pt; color: #777; display: flex; justify-content: space-between; }
    @media print { body { padding: 3mm 4mm; } table thead th { background: #1a3a6a !important; color: white !important; } }
  </style>
</head>
<body>
  <div class="report">
    <div class="header">
      <h1>Primes de mobilisation</h1>
      <div class="subtitle">2 $ par collecte</div>
      <div class="meta">
        Période: ${formatPeriod()} | 
        ${new Date().toLocaleDateString('fr-FR')} | 
        ${new Date().toLocaleTimeString('fr-FR')}
      </div>
    </div>
    
    <div class="section">
      <div class="section-title">1. Synthèse des indicateurs</div>
      <div class="stats-grid">
        <div class="stat-item"><div class="label">Total agents</div><div class="value primary">${allAgents.length}</div></div>
        <div class="stat-item"><div class="label">Collectes</div><div class="value primary">${totalCollectes}</div></div>
        <div class="stat-item"><div class="label">Taxes collectées</div><div class="value primary">${formatCurrencyPDF(totalTaxes)}</div></div>
        <div class="stat-item"><div class="label">Primes payées</div><div class="value success">${paidCount}/${allAgents.length}</div></div>
      </div>
    </div>

    <div class="section">
      <div class="section-title">2. Synthèse comptable</div>
      <div class="accounting-summary">
        <div class="row">
          <div class="col"><div class="label">DÉBIT</div><div class="value debit">${formatCurrencyPDF(totalDebit)}</div></div>
          <div class="col"><div class="label">CRÉDIT</div><div class="value credit">${formatCurrencyPDF(totalCredit)}</div></div>
          <div class="col"><div class="label">SOLDE</div><div class="value balance">${formatCurrencyPDF(Math.abs(totalBalance))}</div></div>
          <div class="col"><div class="label">STATUT</div><div class="value"><span class="badge ${totalBalance <= 0 ? 'paid' : 'pending'}">${totalBalance <= 0 ? 'SOLDÉ' : 'NON SOLDÉ'}</span></div></div>
        </div>
      </div>
    </div>
    
    <div class="section">
      <div class="section-title">3. Liste des agents (${allAgents.length})</div>
      <table>
        <thead>
          <tr><th style="width:30px;">#</th><th>Agent</th><th>Email</th><th>Collectes</th><th>Taxes</th><th>DÉBIT</th><th>CRÉDIT</th><th>Solde</th><th>Statut</th></tr>
        </thead>
        <tbody>
          ${allAgents.map((agent, index) => {
            const bonus = agent.bonus;
            const debit = bonus ? parseFloat(bonus.total_amount || 0) : 0;
            const credit = bonus ? parseFloat(bonus.total_paid || 0) : 0;
            const balance = debit - credit;
            const isSettled = balance <= 0;
            const statusLabel = bonus?.is_fully_paid ? 'Payée' : 'En attente';
            return `
            <tr>
              <td class="text-center">${index + 1}</td>
              <td><strong>${agent.nom_complet || 'N/A'}</strong></td>
              <td>${agent.email || 'N/A'}</td>
              <td class="text-center">${bonus?.collectes_count || 0}</td>
              <td class="text-right">${formatCurrencyPDF(bonus?.taxes_collected || 0)}</td>
              <td class="text-right fw-bold" style="color:#dc3545;">${bonus ? formatCurrencyPDF(debit) : '-'}</td>
              <td class="text-right fw-bold" style="color:#28a745;">${bonus ? formatCurrencyPDF(credit) : '-'}</td>
              <td class="text-right fw-bold" style="color:${isSettled ? '#28a745' : '#ffc107'};">${bonus ? formatCurrencyPDF(Math.abs(balance)) : '-'}</td>
              <td class="text-center"><span class="badge ${isSettled ? 'paid' : 'pending'}">${statusLabel}</span></td>
            </tr>
            `;
          }).join('')}
        </tbody>
      </table>
    </div>
    
    <div class="footer">
      <span>Généré par SyncTaxe</span>
      <span>${new Date().toLocaleDateString('fr-FR')}</span>
    </div>
  </div>
</body>
</html>`;

      const iframe = document.createElement('iframe');
      iframe.style.position = 'absolute';
      iframe.style.width = '0';
      iframe.style.height = '0';
      iframe.style.border = 'none';
      document.body.appendChild(iframe);
      
      const iframeDoc = iframe.contentWindow.document;
      iframeDoc.open();
      iframeDoc.write(htmlContent);
      iframeDoc.close();
      
      iframe.contentWindow.onload = () => {
        iframe.contentWindow.document.title = fileName;
        iframe.contentWindow.print();
        
        setTimeout(() => {
          document.title = originalTitle;
          if (document.body.contains(iframe)) document.body.removeChild(iframe);
          setExporting(false);
          showToastNotification('✅ Succès', `PDF exporté: ${allAgents.length} agents`, 'success');
        }, 1500);
      };
      
    } catch (err) {
      console.error('Erreur export PDF:', err);
      showToastNotification('❌ Erreur', 'Erreur lors de l\'export PDF: ' + err.message, 'danger');
      setExporting(false);
    }
  };

  // ==================== PAGINATION ====================
  const handlePageChange = useCallback((page) => {
    if (page >= 1 && page <= pagination.lastPage) {
      setCurrentPage(page);
      fetchDirect(page, searchTerm, startDate, endDate, itemsPerPage, true, true);
    }
  }, [pagination.lastPage, searchTerm, startDate, endDate, itemsPerPage, fetchDirect]);

  const getPaginationItems = () => {
    const items = [];
    const maxVisible = 5;
    const current = pagination.currentPage;
    const total = pagination.lastPage;
    
    if (total <= 1) return items;
    
    let startPage = Math.max(1, current - Math.floor(maxVisible / 2));
    let endPage = Math.min(total, startPage + maxVisible - 1);
    
    if (endPage - startPage + 1 < maxVisible) {
      startPage = Math.max(1, endPage - maxVisible + 1);
    }

    if (startPage > 1) {
      items.push(
        <Pagination.First key="first" onClick={() => handlePageChange(1)} />
      );
      items.push(
        <Pagination.Prev key="prev" onClick={() => handlePageChange(current - 1)} />
      );
    }

    for (let i = startPage; i <= endPage; i++) {
      items.push(
        <Pagination.Item key={i} active={i === current} onClick={() => handlePageChange(i)}>
          {i}
        </Pagination.Item>
      );
    }

    if (endPage < total) {
      items.push(
        <Pagination.Next key="next" onClick={() => handlePageChange(current + 1)} />
      );
      items.push(
        <Pagination.Last key="last" onClick={() => handlePageChange(total)} />
      );
    }

    return items;
  };

  // ==================== STAT CARD ====================
  const StatCard = ({ title, value, icon, color, subtitle, progress }) => (
    <Col md={3} sm={6} className="mb-3">
      <Card className="h-100 border-0 shadow-sm stats-card">
        <div className={`card-gradient bg-${color}`}></div>
        <Card.Body>
          <div className="d-flex justify-content-between align-items-start">
            <div>
              <h6 className="text-muted mb-2 small text-uppercase fw-bold">{title}</h6>
              <h3 className="mb-0 fw-bold">{value}</h3>
              {subtitle && <small className="text-muted">{subtitle}</small>}
              {progress !== undefined && (
                <ProgressBar 
                  now={progress} 
                  variant={progress >= 80 ? 'success' : progress >= 50 ? 'warning' : 'danger'}
                  className="mt-2"
                  style={{ height: '6px' }}
                />
              )}
            </div>
            <div className={`bg-${color} bg-opacity-10 rounded-circle p-3`}>
              {icon}
            </div>
          </div>
        </Card.Body>
      </Card>
    </Col>
  );

  // ==================== USEEFFECT ====================
  useEffect(() => {
    setStats(computedStats);
    setAccountingData(computedAccountingData);
  }, [computedStats, computedAccountingData]);

  // ==================== CHARGEMENT INITIAL ====================
  useEffect(() => {
    let isMounted = true;
    let isInitialized = false;
    
    const initializePage = async () => {
      if (isInitialized) return;
      isInitialized = true;
      if (!isMounted) return;
      const today = new Date().toISOString().slice(0, 10);
      await fetchDirect(1, '', today, today, 10, false, true);
    };
    
    initializePage();
    
    return () => {
      isMounted = false;
    };
  }, []);

  // ==================== CHARGEMENT ====================
  if (loading || calculating || isRecalculating) {
    return (
      <>
        <Header />
        <div className="main main-app p-3 p-lg-4" style={{ background: '#f0f2f5', minHeight: '100vh' }}>
          <Container className="d-flex justify-content-center align-items-center" style={{ minHeight: '60vh' }}>
            <Card className="border-0 shadow-lg rounded-4 text-center" style={{ maxWidth: '500px', width: '100%' }}>
              <Card.Body className="p-5">
                <div className="mb-4">
                  <div className="pulse-animation mx-auto d-flex align-items-center justify-content-center" 
                       style={{ 
                         width: '90px', 
                         height: '90px', 
                         background: 'linear-gradient(135deg, #0d6efd, #0a58ca)', 
                         borderRadius: '50%',
                         boxShadow: '0 10px 40px rgba(13, 110, 253, 0.3)'
                       }}>
                    <BarChartFill size={40} className="text-white" />
                  </div>
                </div>
                
                <h4 className="fw-bold mb-2">Chargement en cours</h4>
                <p className="text-muted mb-4">
                  {isRecalculating ? 'Recalcul des primes en cours...' : 'Récupération des primes de mobilisation...'}
                </p>
                
                <div className="d-flex justify-content-center gap-2 mb-4">
                  <div className="spinner-grow spinner-grow-sm text-primary" style={{ animationDelay: '0s' }} />
                  <div className="spinner-grow spinner-grow-sm text-primary" style={{ animationDelay: '0.15s' }} />
                  <div className="spinner-grow spinner-grow-sm text-primary" style={{ animationDelay: '0.3s' }} />
                  <div className="spinner-grow spinner-grow-sm text-primary" style={{ animationDelay: '0.45s' }} />
                  <div className="spinner-grow spinner-grow-sm text-primary" style={{ animationDelay: '0.6s' }} />
                </div>
                
                <div className="mx-auto" style={{ width: '200px' }}>
                  <div className="progress" style={{ height: '4px', borderRadius: '2px' }}>
                    <div 
                      className="progress-bar progress-bar-striped progress-bar-animated bg-primary" 
                      style={{ width: '100%' }}
                    />
                  </div>
                </div>
                
                <div className="mt-4">
                  <small className="text-muted">
                    <InfoCircle className="me-1" size={12} />
                    Cette opération peut prendre quelques secondes
                  </small>
                </div>
              </Card.Body>
            </Card>
          </Container>
          
          <style>{`
            .pulse-animation { animation: pulse 1.5s ease-in-out infinite; }
            @keyframes pulse {
              0% { transform: scale(1); opacity: 1; box-shadow: 0 0 0 0 rgba(13,110,253,0.4); }
              50% { transform: scale(1.05); opacity: 0.9; box-shadow: 0 0 0 20px rgba(13,110,253,0); }
              100% { transform: scale(1); opacity: 1; box-shadow: 0 0 0 0 rgba(13,110,253,0); }
            }
            .spinner-grow { animation-duration: 1.2s; }
          `}</style>
          
          <Footer />
        </div>
      </>
    );
  }

  // ==================== RENDU PRINCIPAL ====================
  return (
    <>
      <Header />
      <div className="main main-app p-3 p-lg-4" style={{ background: '#f0f2f5', minHeight: '100vh' }}>
        <Container fluid>
          {/* Toast */}
          <ToastContainer className="p-3" style={{ zIndex: 9999, position: 'fixed', top: '20px', right: '20px' }}>
            <Toast show={showToast} onClose={() => setShowToast(false)} bg={toastVariant} delay={5000} autohide>
              <Toast.Header>
                <strong className="me-auto">{toastTitle}</strong>
              </Toast.Header>
              <Toast.Body className={toastVariant === 'success' ? 'text-white' : ''}>
                {toastMessage}
              </Toast.Body>
            </Toast>
          </ToastContainer>

          {/* En-tête avec exports */}
          <Row className="mb-4">
            <Col md={12}>
              <div className="d-flex flex-column flex-md-row justify-content-between align-items-start align-items-md-center">
                <div className="mb-3 mb-md-0">
                  <ol className="breadcrumb fs-sm mb-1">
                    <li className="breadcrumb-item"><a href="#" onClick={() => navigate('/superAdmin/Agents')}>Agents</a></li>
                    <li className="breadcrumb-item active" aria-current="page">Primes de mobilisation</li>
                  </ol>
                  <h4 className="main-title mb-0 d-flex align-items-center">
                    <BarChartFill className="me-2 text-primary" size={28} />
                    Primes de mobilisation
                  </h4>
                  <p className="text-muted mb-0">2 $ par collecte</p>
                </div>

                <div className="d-flex flex-wrap gap-2">
                  <Button 
                    variant="primary" 
                    size="sm"
                    onClick={handleRegenerateBonuses} 
                    disabled={calculating}
                    className="d-flex align-items-center shadow-sm"
                  >
                    {calculating ? <Spinner animation="border" size="sm" className="me-2" /> : <ArrowRepeat className="me-2" size={16} />}
                    Actualiser
                  </Button>
                  <Button 
                    variant="warning"
                    size="sm"
                    onClick={handleRegenerateFirstTen}
                    disabled={isRecalculating || groupedBonuses.length === 0}
                    className="d-flex align-items-center shadow-sm"
                  >
                    {isRecalculating ? <Spinner animation="border" size="sm" className="me-2" /> : <ArrowRepeat className="me-2" size={16} />}
                    10 premiers
                  </Button>
                  <Button 
                    variant="info" 
                    size="sm"
                    onClick={openCalculateModal}
                    disabled={groupedBonuses.length === 0}
                    className="d-flex align-items-center shadow-sm text-white"
                  >
                    <Person className="me-2" size={16} />
                    Calculer agent
                  </Button>
                  <Button 
                    variant="success" 
                    size="sm"
                    className="d-flex align-items-center"
                    onClick={handleExportExcel}
                    disabled={exporting || groupedBonuses.length === 0}
                  >
                    {exporting ? <Spinner animation="border" size="sm" className="me-2" /> : <FileEarmarkExcel className="me-2" size={16} />}
                    Excel
                  </Button>
                  <Button 
                    variant="danger" 
                    size="sm"
                    className="d-flex align-items-center"
                    onClick={handleExportPDF}
                    disabled={exporting || groupedBonuses.length === 0}
                  >
                    {exporting ? <Spinner animation="border" size="sm" className="me-2" /> : <FilePdf className="me-2" size={16} />}
                    PDF
                  </Button>
                </div>
              </div>
            </Col>
          </Row>

          {error && (
            <Alert variant="danger" className="mb-4 border-0 shadow-sm">
              <XCircle className="me-2" />
              {error}
            </Alert>
          )}

          {isRecalculating && (
            <Alert variant="info" className="mb-4 border-0 shadow-sm d-flex align-items-center">
              <Spinner animation="border" size="sm" className="me-2" />
              <span>Recalcul des primes en cours pour la période sélectionnée...</span>
            </Alert>
          )}

          {/* Stats */}
          {stats && stats.total_agents > 0 ? (
            <Row className="g-3 mb-4">
              <StatCard
                title="Agents"
                value={stats.total_agents}
                icon={<Person className="text-primary" size={22} />}
                color="primary"
              />
              <StatCard
                title="Collectes"
                value={stats.total_collectes ?? 0}
                icon={<Receipt className="text-success" size={22} />}
                color="success"
              />
              <StatCard
                title="Taxes collectées"
                value={formatCurrency(stats.total_taxes_collected)}
                icon={<CurrencyDollar className="text-info" size={22} />}
                color="info"
              />
              <StatCard
                title="Total primes"
                value={formatCurrency(stats.total_amount)}
                icon={<Wallet2 className="text-warning" size={22} />}
                color="warning"
                subtitle={`${stats.fixed_rate_per_collecte || 2} $/collecte · Payé: ${formatCurrency(stats.paid_amount)}`}
                progress={stats.total_amount > 0 ? Math.round((stats.paid_amount / stats.total_amount) * 100) : 0}
              />
            </Row>
          ) : (
            <Row className="g-3 mb-4">
              <Col md={12}>
                <Alert variant="info" className="border-0 shadow-sm">
                  <InfoCircle className="me-2" size={20} />
                  <span>Aucune prime de mobilisation trouvée pour la période sélectionnée.</span>
                </Alert>
              </Col>
            </Row>
          )}

          {/* Synthèse comptable */}
          {accountingData && accountingData.totalBonuses > 0 && (
            <Row className="mb-4">
              <Col md={12}>
                <Card className={`border-0 shadow-sm rounded-3 ${accountingData.isSettled ? 'border-success border-2' : 'border-warning border-2'}`}>
                  <Card.Body>
                    <div className="d-flex flex-column flex-md-row justify-content-between align-items-start align-items-md-center gap-3">
                      <div>
                        <h6 className="fw-bold mb-2 d-flex align-items-center">
                          <Wallet2 className="me-2" size={20} />
                          Synthèse comptable - {formatPeriod()}
                        </h6>
                        <div className="d-flex flex-wrap gap-4">
                          <div>
                            <span className="text-muted small text-uppercase d-block">DÉBIT (Primes dues)</span>
                            <span className="h5 fw-bold text-danger">{formatCurrency(accountingData.debit)}</span>
                          </div>
                          <div>
                            <span className="text-muted small text-uppercase d-block">CRÉDIT (Payé)</span>
                            <span className="h5 fw-bold text-success">{formatCurrency(accountingData.credit)}</span>
                          </div>
                          <div>
                            <span className="text-muted small text-uppercase d-block">SOLDE</span>
                            <span className={`h5 fw-bold ${accountingData.balance > 0 ? 'text-warning' : 'text-success'}`}>
                              {formatCurrency(Math.abs(accountingData.balance))}
                              {accountingData.balance > 0 ? ' (Restant dû)' : ' (Crédit excédentaire)'}
                            </span>
                          </div>
                        </div>
                      </div>
                      <div className="d-flex align-items-center gap-3">
                        <Badge 
                          bg={accountingData.isSettled ? 'success' : 'warning'} 
                          pill 
                          className="px-4 py-2 fs-6"
                        >
                          {accountingData.isSettled ? (
                            <><CheckCircle className="me-1" size={16} /> COMPLÈTEMENT SOLDÉ</>
                          ) : (
                            <><Clock className="me-1" size={16} /> NON SOLDÉ</>
                          )}
                        </Badge>
                        <Button
                          variant="outline-primary"
                          size="sm"
                          onClick={() => setShowAccountingModal(true)}
                          className="d-flex align-items-center"
                        >
                          <InfoCircle className="me-1" size={16} />
                          Détails
                        </Button>
                      </div>
                    </div>
                  </Card.Body>
                </Card>
              </Col>
            </Row>
          )}

          {/* Filtres */}
          <Row className="mb-4">
            <Col md={12}>
              <Card className="border-0 shadow-sm rounded-3">
                <Card.Body>
                  <Row className="g-3">
                    <Col md={2}>
                      <Form.Group>
                        <Form.Label className="fw-bold small">
                          <Calendar className="me-1" />
                          Date de début
                          <span className="text-danger ms-1">*</span>
                        </Form.Label>
                        <Form.Control
                          type="date"
                          value={tempStartDate}
                          onChange={(e) => setTempStartDate(e.target.value)}
                          className={`bg-light border-0 ${!tempStartDate ? 'border-danger' : ''}`}
                        />
                      </Form.Group>
                    </Col>
                    <Col md={2}>
                      <Form.Group>
                        <Form.Label className="fw-bold small">
                          <Calendar className="me-1" />
                          Date de fin
                          <span className="text-danger ms-1">*</span>
                        </Form.Label>
                        <Form.Control
                          type="date"
                          value={tempEndDate}
                          onChange={(e) => setTempEndDate(e.target.value)}
                          className={`bg-light border-0 ${!tempEndDate ? 'border-danger' : ''}`}
                        />
                      </Form.Group>
                    </Col>
                    <Col md={3}>
                      <Form.Group>
                        <Form.Label className="fw-bold small">
                          <Search className="me-1" />
                          Rechercher
                        </Form.Label>
                        <InputGroup>
                          <InputGroup.Text className="bg-light border-0">
                            <Search />
                          </InputGroup.Text>
                          <Form.Control
                            placeholder="Agent..."
                            value={tempSearchTerm}
                            onChange={(e) => setTempSearchTerm(e.target.value)}
                            className="bg-light border-0"
                          />
                          {tempSearchTerm && (
                            <Button 
                              variant="outline-secondary" 
                              className="border-0"
                              onClick={() => setTempSearchTerm('')}
                            >
                              <XCircle size={16} />
                            </Button>
                          )}
                        </InputGroup>
                      </Form.Group>
                    </Col>
                    <Col md={2}>
                      <Form.Group>
                        <Form.Label className="fw-bold small">Par page</Form.Label>
                        <Form.Select
                          value={tempItemsPerPage}
                          onChange={(e) => setTempItemsPerPage(Number(e.target.value))}
                          className="bg-light border-0"
                        >
                          <option value="5">5</option>
                          <option value="10">10</option>
                          <option value="25">25</option>
                          <option value="50">50</option>
                          <option value="100">100</option>
                        </Form.Select>
                      </Form.Group>
                    </Col>
                    <Col md={3}>
                      <Form.Label className="fw-bold small">&nbsp;</Form.Label>
                      <div className="d-flex gap-2 flex-wrap">
                        <Button 
                          variant="primary" 
                          onClick={handleApplyFilters}
                          className="d-flex align-items-center"
                          disabled={isLoadingRef.current || isRecalculating}
                        >
                          {isLoadingRef.current ? (
                            <Spinner animation="border" size="sm" className="me-2" />
                          ) : (
                            <Search className="me-2" size={16} />
                          )}
                          Valider les filtres
                        </Button>
                        <Button 
                          variant="outline-secondary" 
                          onClick={handleResetFilters}
                          className="d-flex align-items-center"
                          disabled={isLoadingRef.current}
                        >
                          <ArrowRepeat className="me-0" size={16} />
                        </Button>
                      </div>
                    </Col>
                  </Row>
                  
                  <Row className="mt-2">
                    <Col md={12}>
                      <small className="text-muted">
                        <strong>Période :</strong> {formatPeriod()}
                        {searchTerm && <Badge bg="info" className="ms-2">Filtre: {searchTerm}</Badge>}
                        {isFilterApplied && <Badge bg="secondary" className="ms-2">Filtres appliqués</Badge>}
                        {isRecalculating && <Badge bg="info" className="ms-2"><Spinner animation="border" size="sm" className="me-1" /> Recalcul...</Badge>}
                      </small>
                    </Col>
                  </Row>
                </Card.Body>
              </Card>
            </Col>
          </Row>

          {/* Tableau */}
          <Row>
            <Col md={12}>
              <Card className="shadow-sm border-0 rounded-3">
                <Card.Header className="bg-primary d-flex justify-content-between align-items-center py-3 border-bottom">
                  <div className="d-flex align-items-center">
                    <Briefcase className="me-2 text-white" />
                    <span className="fw-semibold text-white">
                      Liste des agents ({pagination.total})
                    </span>
                    {searchTerm && <Badge bg="info" className="ms-2 rounded-pill">Filtre actif</Badge>}
                    {accountingData && accountingData.totalBonuses > 0 && (
                      <Badge 
                        bg={accountingData.isSettled ? 'success' : 'warning'} 
                        className="ms-2 rounded-pill px-3 py-1"
                      >
                        {accountingData.isSettled ? 'Soldé' : 'Non soldé'}
                      </Badge>
                    )}
                    {isRecalculating && (
                      <Badge bg="warning" className="ms-2 rounded-pill px-3 py-1">
                        <Spinner animation="border" size="sm" className="me-1" /> Mise à jour...
                      </Badge>
                    )}
                    {isFilterApplied && (
                      <Badge bg="secondary" className="ms-2 rounded-pill">Filtres actifs</Badge>
                    )}
                  </div>
                  <div className="text-white small d-flex align-items-center">
                    {stats && stats.total_amount > 0 && (
                      <span className="me-3">Total primes: {formatCurrency(accountingData?.debit || stats.total_amount)}</span>
                    )}
                    {accountingData && accountingData.balance > 0 && (
                      <span className="me-2 text-warning">
                        Reste: {formatCurrency(accountingData.balance)}
                      </span>
                    )}
                    <span className="me-2">|</span>
                    <span>Affichage {pagination.from}-{pagination.to} / {pagination.total}</span>
                  </div>
                </Card.Header>
                
                <Card.Body className="p-0">
                  {groupedBonuses.length === 0 ? (
                    <div className="text-center py-5">
                      <InfoCircle size={48} className="text-muted opacity-25 mb-3" />
                      <h5 className="text-muted">Aucune prime de mobilisation</h5>
                      <p className="text-muted mb-0">
                        Aucune prime pour la période <strong>{formatPeriod()}</strong>
                      </p>
                      <Button 
                        variant="primary" 
                        className="mt-3"
                        onClick={handleRegenerateBonuses}
                        disabled={calculating}
                      >
                        {calculating ? <Spinner animation="border" size="sm" className="me-2" /> : <Calendar className="me-2" size={16} />}
                        Générer les primes
                      </Button>
                    </div>
                  ) : (
                    <>
                      <div className="table-responsive">
                        <Table hover className="mb-0 align-middle">
                          <thead className="bg-light">
                            <tr>
                              <th>Agent</th>
                              <th className="text-center">Collectes</th>
                              <th className="text-center">Taxes</th>
                              <th className="text-center">DÉBIT (Prime)</th>
                              <th className="text-center">CRÉDIT (Payé)</th>
                              <th className="text-center">Solde</th>
                              <th className="text-center">Statut</th>
                              <th className="text-center">Actions</th>
                            </tr>
                          </thead>
                          <tbody>
                            {groupedBonuses.map((group) => {
                              const { total_debit, total_credit, balance, isSettled, isFullyPaid, remainingBalance } = group;
                              
                              return (
                                <tr key={group.user_id}>
                                  <td>
                                    <div className="d-flex align-items-center">
                                      <div className="bg-primary bg-opacity-10 rounded-circle p-2 me-2">
                                        <Person size={14} className="text-primary" />
                                      </div>
                                      <div>
                                        <strong>{group.agent?.name || `Agent ${group.user_id}`}</strong>
                                        <br />
                                        <small className="text-muted">{group.agent?.email || 'N/A'}</small>
                                      </div>
                                    </div>
                                  </td>
                                  <td className="text-center">
                                    <Badge bg="success" pill className="px-3 py-2">
                                      <Receipt className="me-1" size={12} />
                                      {group.total_collectes || 0}
                                    </Badge>
                                  </td>
                                  <td className="text-center">
                                    <span className="fw-bold">{formatCurrency(group.total_taxes || 0)}</span>
                                  </td>
                                  <td className="text-center">
                                    <span className="text-danger fw-bold">
                                      {formatCurrency(total_debit)}
                                    </span>
                                    <br />
                                    <small className="text-muted">
                                      {group.total_collectes} × {group.fixed_rate_per_collecte || 2} $
                                    </small>
                                  </td>
                                  <td className="text-center">
                                    <span className="text-success fw-bold">
                                      {formatCurrency(total_credit)}
                                    </span>
                                  </td>
                                  <td className="text-center">
                                    <span className={`fw-bold ${isSettled ? 'text-success' : 'text-warning'}`}>
                                      {formatCurrency(Math.abs(balance))}
                                      {!isSettled && <span className="text-muted ms-1">(dû)</span>}
                                      {isSettled && balance === 0 && <span className="text-success ms-1">✓</span>}
                                    </span>
                                  </td>
                                  <td className="text-center">
                                    {(() => {
                                      const hasBonus = group.total_debit > 0 || group.bonuses.length > 0;
                                      
                                      if (hasBonus) {
                                        if (isFullyPaid) {
                                          return (
                                            <Badge bg="success" pill className="px-3 py-2">
                                              <CheckCircle className="me-1" size={12} />
                                              Payée
                                            </Badge>
                                          );
                                        } else {
                                          return (
                                            <Badge bg="warning" pill className="px-3 py-2">
                                              <Clock className="me-1" size={12} />
                                              En attente
                                            </Badge>
                                          );
                                        }
                                      } else {
                                        return (
                                          <Badge bg="secondary" pill className="px-3 py-2">
                                            <Clock className="me-1" size={12} />
                                            Non calculée
                                          </Badge>
                                        );
                                      }
                                    })()}
                                  </td>
                                  <td className="text-center">
                                    <div className="d-flex gap-1 justify-content-center">
                                      {!isFullyPaid && total_debit > 0 && (
                                        <Button
                                          variant="outline-success"
                                          size="sm"
                                          onClick={() => {
                                            const bonusToPay = group.bonuses.find(b => b.status === 'pending') || group.bonuses[0];
                                            
                                            setSelectedBonus({
                                              ...bonusToPay,
                                              amount: remainingBalance,
                                              remaining_balance: remainingBalance,
                                              total_amount: total_debit,
                                              total_paid: total_credit,
                                              agent: group.agent,
                                              user_id: group.user_id
                                            });
                                            setShowPayModal(true);
                                          }}
                                          className="rounded-circle"
                                          style={{ width: '34px', height: '34px', padding: 0 }}
                                          title={`Payer ${formatCurrency(remainingBalance)} restant`}
                                        >
                                          <Wallet2 size={14} />
                                        </Button>
                                      )}
                                      
                                      <Button
                                        variant="outline-primary"
                                        size="sm"
                                        onClick={() => {
                                          const fullAgent = {
                                            user_id: group.user_id,
                                            nom_complet: group.agent?.name || `Agent ${group.user_id}`,
                                            email: group.agent?.email || 'N/A',
                                            telephone: group.agent?.phone || 'N/A'
                                          };
                                          setSelectedAgent(fullAgent);
                                          setShowCalculateModal(true);
                                        }}
                                        className="rounded-circle"
                                        style={{ width: '34px', height: '34px', padding: 0 }}
                                        title="Calculer la prime"
                                      >
                                        <ArrowRepeat size={14} />
                                      </Button>
                                    </div>
                                  </td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </Table>
                      </div>

                      {pagination.total > 0 && (
                        <div className="p-3 border-top d-flex flex-column flex-sm-row justify-content-between align-items-center gap-3">
                          <div className="text-muted small">
                            Affichage de <strong>{pagination.from}</strong> à <strong>{pagination.to}</strong> sur <strong>{pagination.total}</strong> agents
                          </div>
                          <div className="d-flex align-items-center gap-3">
                            <span className="text-muted small">
                              Total débit: <strong className="text-danger">{formatCurrency(
                                groupedBonuses.reduce((sum, g) => sum + g.total_debit, 0)
                              )}</strong>
                            </span>
                            <span className="text-muted small">
                              Total crédit: <strong className="text-success">{formatCurrency(
                                groupedBonuses.reduce((sum, g) => sum + g.total_credit, 0)
                              )}</strong>
                            </span>
                            <Pagination className="mb-0">{getPaginationItems()}</Pagination>
                          </div>
                        </div>
                      )}
                    </>
                  )}
                </Card.Body>
              </Card>
            </Col>
          </Row>
        </Container>

        {/* MODALES */}

        {/* Modal de paiement */}
        <Modal show={showPayModal} onHide={() => setShowPayModal(false)} centered>
          <Modal.Header closeButton className="border-0" style={{ background: "linear-gradient(135deg, #0d6efd, #0b5ed7)" }}>
            <Modal.Title className="text-white d-flex align-items-center">
              <Wallet2 className="me-2" />
              Payer la prime
            </Modal.Title>
          </Modal.Header>
          <Modal.Body className="p-4">
            {selectedBonus && (
              <>
                <div className="bg-light p-3 rounded mb-3">
                  <Row className="small">
                    <Col md={6}>
                      <span className="text-muted">Agent</span>
                      <p className="fw-bold mb-0">{selectedBonus.agent?.name || `Agent ${selectedBonus.user_id}`}</p>
                    </Col>
                    <Col md={6}>
                      <span className="text-muted">Montant à payer</span>
                      <p className="fw-bold text-success mb-0">
                        {formatCurrency(selectedBonus.amount || selectedBonus.remaining_balance || 0)}
                      </p>
                    </Col>
                  </Row>
                  <Row className="mt-2">
                    <Col md={6}>
                      <span className="text-muted">Collectes</span>
                      <p className="fw-bold mb-0">{selectedBonus.collectes_count || 0}</p>
                    </Col>
                    <Col md={6}>
                      <span className="text-muted">Période</span>
                      <p className="fw-bold mb-0">{formatPeriod()}</p>
                    </Col>
                  </Row>
                  {(selectedBonus.total_amount || selectedBonus.total_paid !== undefined) && (
                    <Row className="mt-2 pt-2 border-top">
                      <Col md={6}>
                        <span className="text-muted">Total dû</span>
                        <p className="fw-bold mb-0">{formatCurrency(selectedBonus.total_amount || 0)}</p>
                      </Col>
                      <Col md={6}>
                        <span className="text-muted">Déjà payé</span>
                        <p className="fw-bold mb-0 text-success">{formatCurrency(selectedBonus.total_paid || 0)}</p>
                      </Col>
                    </Row>
                  )}
                  {selectedBonus.remaining_balance !== undefined && selectedBonus.remaining_balance > 0 && (
                    <Row className="mt-2 pt-2 border-top border-warning">
                      <Col md={12}>
                        <div className="text-center">
                          <span className="text-muted">Solde restant à payer</span>
                          <p className="fw-bold text-warning mb-0" style={{ fontSize: '1.3rem' }}>
                            {formatCurrency(selectedBonus.remaining_balance)}
                          </p>
                        </div>
                      </Col>
                    </Row>
                  )}
                  <Row className="mt-2 pt-2 border-top">
                    <Col md={6}>
                      <span className="text-muted">Taxes collectées</span>
                      <p className="fw-bold mb-0">{formatCurrency(selectedBonus.taxes_collected || 0)}</p>
                    </Col>
                    <Col md={6}>
                      <span className="text-muted">Taux</span>
                      <p className="fw-bold mb-0">
                        {selectedBonus.fixed_rate_per_collecte || 2} $/collecte
                      </p>
                    </Col>
                  </Row>
                </div>
                
                <Form.Group className="mb-3">
                  <Form.Label className="fw-bold small">Mode de paiement</Form.Label>
                  <div className="d-flex gap-3">
                    <Form.Check
                      type="radio"
                      id="payment-bank"
                      label="Banque"
                      value="bank_transfer"
                      checked={paymentMethod === 'bank_transfer'}
                      onChange={(e) => {
                        setPaymentMethod(e.target.value);
                        setPaymentReference('');
                        setMobileNumber('');
                      }}
                      className="fw-bold"
                    />
                    <Form.Check
                      type="radio"
                      id="payment-mobile"
                      label="Mobile Money"
                      value="orange_money"
                      checked={paymentMethod === 'orange_money' || paymentMethod === 'mtn_money' || paymentMethod === 'wave'}
                      onChange={(e) => {
                        setPaymentMethod(e.target.value);
                        setPaymentReference('');
                        setMobileNumber('');
                      }}
                      className="fw-bold"
                    />
                  </div>
                </Form.Group>

                {paymentMethod === 'bank_transfer' ? (
                  <Form.Group className="mb-3">
                    <Form.Label className="fw-bold small">
                      <FileText className="me-1" size={14} />
                      Référence du virement
                    </Form.Label>
                    <Form.Control
                      type="text"
                      placeholder="Entrez la référence du virement bancaire..."
                      value={paymentReference}
                      onChange={(e) => setPaymentReference(e.target.value)}
                      className="border-0 bg-light py-2"
                      required
                    />
                  </Form.Group>
                ) : (paymentMethod === 'orange_money' || paymentMethod === 'mtn_money' || paymentMethod === 'wave') ? (
                  <>
                    <Form.Group className="mb-3">
                      <Form.Label className="fw-bold small">
                        <Phone className="me-1" size={14} />
                        Opérateur Mobile Money
                      </Form.Label>
                      <Form.Select
                        value={mobileOperator}
                        onChange={(e) => {
                          const operator = e.target.value;
                          setMobileOperator(operator);
                          setPaymentMethod(operator);
                        }}
                        className="border-0 bg-light py-2"
                      >
                        <option value="orange_money">Orange Money</option>
                        <option value="mtn_money">MTN Mobile Money</option>
                        <option value="wave">Wave</option>
                      </Form.Select>
                    </Form.Group>
                    <Form.Group className="mb-3">
                      <Form.Label className="fw-bold small">
                        <Phone className="me-1" size={14} />
                        Numéro Mobile Money
                      </Form.Label>
                      <Form.Control
                        type="tel"
                        placeholder="Entrez le numéro Mobile Money"
                        value={mobileNumber}
                        onChange={(e) => setMobileNumber(e.target.value)}
                        className="border-0 bg-light py-2"
                        required
                      />
                    </Form.Group>
                  </>
                ) : null}

                <div className="mt-3 p-3 bg-success bg-opacity-10 rounded text-center border border-success border-opacity-25">
                  <small className="text-muted text-uppercase">Vous allez payer</small>
                  <p className="h4 fw-bold text-success mb-0">
                    {formatCurrency(selectedBonus.amount || selectedBonus.remaining_balance || 0)}
                  </p>
                  <small className="text-muted">
                    {paymentMethod === 'bank_transfer' ? 'Virement bancaire' : 'Mobile Money'}
                  </small>
                </div>
              </>
            )}
          </Modal.Body>
          <Modal.Footer className="border-0">
            <Button variant="secondary" onClick={() => {
              setShowPayModal(false);
              setPaymentReference('');
              setMobileNumber('');
              setMobileOperator('orange');
              setPaymentNotes('');
            }}>
              Annuler
            </Button>
            <Button 
              variant="success"
              onClick={handlePayBonus}
              disabled={paying || !selectedBonus || parseFloat(selectedBonus.amount || selectedBonus.remaining_balance || 0) <= 0}
            >
              {paying ? (
                <Spinner animation="border" size="sm" className="me-2" />
              ) : (
                <CheckCircle className="me-2" />
              )}
              Confirmer le paiement
            </Button>
          </Modal.Footer>
        </Modal>

        {/* Modal Détails Comptables */}
        <Modal 
          show={showAccountingModal} 
          onHide={() => setShowAccountingModal(false)} 
          centered
          size="lg"
        >
          <Modal.Header closeButton className="border-0" style={{ background: "linear-gradient(135deg, #0d6efd, #0b5ed7)" }}>
            <Modal.Title className="text-white d-flex align-items-center">
              <Wallet2 className="me-2" size={24} />
              Détails comptables
            </Modal.Title>
          </Modal.Header>
          <Modal.Body className="p-4">
            {accountingData && accountingData.totalBonuses > 0 && (
              <>
                <div className="text-center mb-4">
                  <h5 className="fw-bold">Période du {formatPeriod()}</h5>
                  <p className="text-muted small">Suivi des primes de mobilisation</p>
                </div>

                <Row className="g-3 mb-4">
                  <Col md={4}>
                    <Card className="border-0 bg-light text-center h-100">
                      <Card.Body>
                        <div className="text-muted small text-uppercase fw-bold">Total primes</div>
                        <h3 className="fw-bold text-primary">{accountingData.totalBonuses}</h3>
                        <small className="text-muted">primes calculées</small>
                      </Card.Body>
                    </Card>
                  </Col>
                  <Col md={4}>
                    <Card className="border-0 bg-success bg-opacity-10 text-center h-100">
                      <Card.Body>
                        <div className="text-muted small text-uppercase fw-bold">Payées</div>
                        <h3 className="fw-bold text-success">{accountingData.paidCount}</h3>
                        <small className="text-muted">primes payées</small>
                      </Card.Body>
                    </Card>
                  </Col>
                  <Col md={4}>
                    <Card className="border-0 bg-warning bg-opacity-10 text-center h-100">
                      <Card.Body>
                        <div className="text-muted small text-uppercase fw-bold">En attente</div>
                        <h3 className="fw-bold text-warning">{accountingData.pendingCount}</h3>
                        <small className="text-muted">primes non payées</small>
                      </Card.Body>
                    </Card>
                  </Col>
                </Row>

                <Card className="border-0 shadow-sm mb-4">
                  <Card.Body>
                    <Row className="text-center">
                      <Col md={4} className="border-end">
                        <div className="text-muted small text-uppercase">DÉBIT</div>
                        <div className="h4 fw-bold text-danger mb-0">{formatCurrency(accountingData.debit)}</div>
                        <small className="text-muted">Total des primes dues</small>
                      </Col>
                      <Col md={4} className="border-end">
                        <div className="text-muted small text-uppercase">CRÉDIT</div>
                        <div className="h4 fw-bold text-success mb-0">{formatCurrency(accountingData.credit)}</div>
                        <small className="text-muted">Total des primes payées</small>
                      </Col>
                      <Col md={4}>
                        <div className="text-muted small text-uppercase">SOLDE</div>
                        <div className={`h4 fw-bold mb-0 ${accountingData.balance > 0 ? 'text-warning' : 'text-success'}`}>
                          {formatCurrency(Math.abs(accountingData.balance))}
                        </div>
                        <small className="text-muted">
                          {accountingData.balance > 0 ? 'Restant dû' : 'Crédit excédentaire'}
                        </small>
                      </Col>
                    </Row>
                  </Card.Body>
                </Card>

                {accountingData.balance > 0 ? (
                  <Alert variant="warning" className="d-flex align-items-center">
                    <Clock className="me-2" size={20} />
                    <div>
                      <strong>Compte non soldé</strong> - Il reste <strong>{formatCurrency(accountingData.balance)}</strong> à payer pour solder cette période.
                    </div>
                  </Alert>
                ) : (
                  <Alert variant="success" className="d-flex align-items-center">
                    <CheckCircle className="me-2" size={20} />
                    <div>
                      <strong>Compte soldé</strong> - Toutes les primes de la période ont été payées.
                    </div>
                  </Alert>
                )}
              </>
            )}
          </Modal.Body>
          <Modal.Footer className="border-0">
            <Button variant="secondary" onClick={() => setShowAccountingModal(false)}>
              Fermer
            </Button>
            {accountingData && accountingData.balance > 0 && (
              <Button variant="primary" onClick={() => setShowAccountingModal(false)}>
                <Wallet2 className="me-2" />
                Gérer les paiements
              </Button>
            )}
          </Modal.Footer>
        </Modal>

        {/* Modal de calcul pour un agent */}
        <Modal 
          show={showCalculateModal} 
          onHide={() => {
            setShowCalculateModal(false);
            setSelectedAgent(null);
            setAgentSearchTerm('');
          }} 
          centered
          size="lg"
        >
          <Modal.Header closeButton className="border-0" style={{ background: "linear-gradient(135deg, #0dcaf0, #0b5ed7)" }}>
            <Modal.Title className="text-white d-flex align-items-center">
              <Person className="me-2" size={24} />
              Calculer la prime pour un agent
            </Modal.Title>
          </Modal.Header>
          <Modal.Body className="p-4">
            <p className="text-muted mb-3">
              Sélectionnez un agent pour recalculer sa prime de mobilisation pour la période <strong>{formatPeriod()}</strong>
            </p>

            <InputGroup className="mb-3">
              <InputGroup.Text className="bg-white border-end-0">
                <Search className="text-primary" size={18} />
              </InputGroup.Text>
              <Form.Control
                type="text"
                placeholder="Rechercher un agent par nom ou email..."
                value={agentSearchTerm}
                onChange={(e) => {
                  const search = e.target.value.toLowerCase();
                  setAgentSearchTerm(e.target.value);
                  
                  const allAgents = groupedBonuses.map(group => ({
                    user_id: group.user_id,
                    nom_complet: group.agent?.name || `Agent ${group.user_id}`,
                    email: group.agent?.email || 'N/A',
                    telephone: group.agent?.phone || 'N/A',
                    has_bonus: group.total_debit > 0,
                    collectes_count: group.total_collectes || 0,
                    total_amount: group.total_debit,
                    total_paid: group.total_credit,
                    balance: group.balance,
                    status: group.status
                  }));
                  
                  const filtered = allAgents.filter(agent =>
                    agent.nom_complet.toLowerCase().includes(search) ||
                    agent.email.toLowerCase().includes(search) ||
                    agent.user_id.toString().includes(search)
                  );
                  
                  setFilteredAgents(filtered);
                }}
                className="border-start-0 bg-white"
              />
              {agentSearchTerm && (
                <Button
                  variant="outline-secondary"
                  className="border-start-0"
                  onClick={() => {
                    setAgentSearchTerm('');
                    const allAgents = groupedBonuses.map(group => ({
                      user_id: group.user_id,
                      nom_complet: group.agent?.name || `Agent ${group.user_id}`,
                      email: group.agent?.email || 'N/A',
                      telephone: group.agent?.phone || 'N/A',
                      has_bonus: group.total_debit > 0,
                      collectes_count: group.total_collectes || 0,
                      total_amount: group.total_debit,
                      total_paid: group.total_credit,
                      balance: group.balance,
                      status: group.status
                    }));
                    setFilteredAgents(allAgents);
                  }}
                >
                  <XCircle size={16} />
                </Button>
              )}
            </InputGroup>

            <div className="d-flex justify-content-between align-items-center mb-3">
              <span className="text-muted small">
                {filteredAgents.length} agent(s) trouvé(s)
              </span>
              {selectedAgent && (
                <Badge bg="success" className="p-2">
                  <CheckCircle className="me-1" size={12} />
                  {selectedAgent.nom_complet} sélectionné
                </Badge>
              )}
            </div>

            <div className="table-responsive" style={{ maxHeight: '300px', overflowY: 'auto' }}>
              <Table hover className="mb-0">
                <thead className="bg-light sticky-top">
                  <tr>
                    <th style={{ width: '40px' }}>#</th>
                    <th>Agent</th>
                    <th>Email</th>
                    <th>Statut prime</th>
                    <th style={{ width: '120px' }}>Action</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredAgents.length === 0 ? (
                    <tr>
                      <td colSpan="5" className="text-center py-5">
                        <InfoCircle size={48} className="text-muted opacity-25 mb-3" />
                        <p className="text-muted">Aucun agent trouvé</p>
                      </td>
                    </tr>
                  ) : (
                    filteredAgents.map((agent, index) => {
                      const isSelected = selectedAgent?.user_id === agent.user_id;
                      const hasBonus = agent.has_bonus;
                      const statusColor = agent.status === 'paid' ? 'success' : (hasBonus ? 'warning' : 'secondary');
                      const statusLabel = agent.status === 'paid' ? 'Payée' : (hasBonus ? 'En attente' : 'Non calculée');
                      
                      return (
                        <tr 
                          key={agent.user_id}
                          className={isSelected ? 'table-primary' : ''}
                          style={{ cursor: 'pointer' }}
                          onClick={() => {
                            const fullAgent = {
                              user_id: agent.user_id,
                              nom_complet: agent.nom_complet,
                              email: agent.email,
                              telephone: agent.telephone
                            };
                            setSelectedAgent(fullAgent);
                          }}
                        >
                          <td>{index + 1}</td>
                          <td>
                            <div className="d-flex align-items-center">
                              <div className="bg-primary bg-opacity-10 rounded-circle p-2 me-2">
                                <Person size={14} className="text-primary" />
                              </div>
                              <div>
                                <div className="fw-semibold">{agent.nom_complet}</div>
                                <div className="text-muted small">ID: {agent.user_id}</div>
                              </div>
                            </div>
                          </td>
                          <td>
                            <div className="d-flex align-items-center">
                              <Envelope size={12} className="text-muted me-1" />
                              <span className="small">{agent.email}</span>
                            </div>
                            {agent.telephone && agent.telephone !== 'N/A' && (
                              <div className="d-flex align-items-center">
                                <Phone size={12} className="text-muted me-1" />
                                <span className="small text-muted">{agent.telephone}</span>
                              </div>
                            )}
                          </td>
                          <td>
                            <Badge bg={statusColor} pill className="px-3 py-2">
                              {statusLabel}
                            </Badge>
                            {hasBonus && (
                              <>
                                <span className="text-muted small d-block">
                                  Collectes: {agent.collectes_count || 0}
                                </span>
                                <span className="text-muted small d-block">
                                  Dû: {formatCurrency(agent.total_amount)}
                                </span>
                                <span className="text-muted small d-block">
                                  Payé: {formatCurrency(agent.total_paid || 0)}
                                </span>
                                {agent.balance > 0 && (
                                  <span className="text-warning small d-block">
                                    Reste: {formatCurrency(agent.balance)}
                                  </span>
                                )}
                              </>
                            )}
                          </td>
                          <td>
                            {isSelected ? (
                              <Badge bg="success" className="px-3 py-2">
                                <CheckCircle className="me-1" size={12} />
                                Sélectionné
                              </Badge>
                            ) : (
                              <Button
                                variant="outline-primary"
                                size="sm"
                                onClick={() => {
                                  const fullAgent = {
                                    user_id: agent.user_id,
                                    nom_complet: agent.nom_complet,
                                    email: agent.email,
                                    telephone: agent.telephone
                                  };
                                  setSelectedAgent(fullAgent);
                                }}
                                className="rounded-pill"
                              >
                                Sélectionner
                              </Button>
                            )}
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </Table>
            </div>

            {selectedAgent && (
              <div className="mt-3 p-3 bg-light rounded-3 border border-primary">
                <div className="d-flex justify-content-between align-items-center">
                  <div>
                    <strong className="text-primary">Agent sélectionné :</strong>
                    <span className="ms-2">{selectedAgent.nom_complet}</span>
                    <div className="text-muted small">
                      ID: {selectedAgent.user_id} • {selectedAgent.email}
                    </div>
                  </div>
                  <Button
                    variant="outline-secondary"
                    size="sm"
                    onClick={() => {
                      setSelectedAgent(null);
                    }}
                  >
                    <XCircle size={14} className="me-1" />
                    Désélectionner
                  </Button>
                </div>
              </div>
            )}
          </Modal.Body>
          <Modal.Footer className="border-0">
            <Button 
              variant="secondary" 
              onClick={() => {
                setShowCalculateModal(false);
                setSelectedAgent(null);
                setAgentSearchTerm('');
              }}
            >
              Annuler
            </Button>
            <Button 
              variant="info" 
              className="text-white"
              onClick={handleCalculateSingleAgent}
              disabled={!selectedAgent || calculatingSingle}
            >
              {calculatingSingle ? (
                <><Spinner animation="border" size="sm" className="me-2" />Calcul en cours...</>
              ) : (
                <><ArrowRepeat className="me-2" size={16} />Calculer la prime</>
              )}
            </Button>
          </Modal.Footer>
        </Modal>

        {/* Modal de résultat du calcul */}
        <Modal
          show={showResultModal}
          onHide={() => {
            setShowResultModal(false);
            setSingleResult(null);
          }}
          centered
          size="lg"
        >
          <Modal.Header closeButton className="border-0" style={{ background: "linear-gradient(135deg, #198754, #157347)" }}>
            <Modal.Title className="text-white d-flex align-items-center">
              <Award className="me-2" size={24} />
              Résultat du calcul
            </Modal.Title>
          </Modal.Header>
          <Modal.Body className="p-4">
            {singleResult && (
              <>
                <div className="text-center mb-4">
                  <div className="bg-success bg-opacity-10 rounded-circle p-3 d-inline-block mb-3">
                    <CheckCircle size={48} className="text-success" />
                  </div>
                  <h4 className="fw-bold mb-1">Prime calculée avec succès</h4>
                  <p className="text-muted small">
                    Pour la période <strong>{formatPeriod()}</strong>
                  </p>
                </div>

                <Card className="border-0 bg-light mb-4">
                  <Card.Body>
                    <Row>
                      <Col md={8}>
                        <div className="d-flex align-items-center mb-2">
                          <Person className="text-primary me-2" size={18} />
                          <span className="fw-bold">{singleResult.agent_name}</span>
                        </div>
                        <div className="d-flex align-items-center">
                          <Building className="text-muted me-2" size={16} />
                          <small className="text-muted">ID: {singleResult.agent_id}</small>
                        </div>
                      </Col>
                      <Col md={4} className="text-md-end">
                        <Badge bg={singleResult.is_fully_paid ? 'success' : 'warning'} className="p-2" style={{ fontSize: '0.9rem' }}>
                          {singleResult.is_fully_paid ? (
                            <><CheckCircle className="me-1" size={14} /> Payée</>
                          ) : (
                            <><Clock className="me-1" size={14} /> En attente</>
                          )}
                        </Badge>
                        {singleResult.has_complement && (
                          <Badge bg="info" className="ms-1 p-2" style={{ fontSize: '0.8rem' }}>
                            <FileText className="me-1" size={12} />
                            {singleResult.complements?.length || 0} complément(s)
                          </Badge>
                        )}
                      </Col>
                    </Row>
                  </Card.Body>
                </Card>

                <Card className="border-0 shadow-sm mb-4">
                  <Card.Body>
                    <Row className="text-center">
                      <Col md={3} className="border-end">
                        <div className="text-muted small text-uppercase">Taxes collectées</div>
                        <div className="h4 fw-bold text-primary mb-0">
                          {formatCurrency(singleResult.taxes_collected || 0)}
                        </div>
                        <small className="text-muted">Total collecté</small>
                      </Col>
                      <Col md={3} className="border-end">
                        <div className="text-muted small text-uppercase">Collectes</div>
                        <div className="h4 fw-bold text-success mb-0">
                          {singleResult.collectes_count || 0}
                        </div>
                        <small className="text-muted">Paiements effectués</small>
                      </Col>
                      <Col md={3} className="border-end">
                        <div className="text-muted small text-uppercase">DÉBIT (Dû)</div>
                        <div className="h4 fw-bold text-danger mb-0">
                          {formatCurrency(singleResult.total_amount || 0)}
                        </div>
                        <small className="text-muted">
                          {singleResult.collectes_count} × {singleResult.fixed_rate_per_collecte || 2} $
                        </small>
                      </Col>
                      <Col md={3}>
                        <div className="text-muted small text-uppercase">SOLDE</div>
                        <div className={`h4 fw-bold mb-0 ${singleResult.total_pending > 0 ? 'text-warning' : 'text-success'}`}>
                          {formatCurrency(singleResult.total_pending || 0)}
                        </div>
                        <small className="text-muted">
                          {singleResult.total_pending > 0 ? 'Restant dû' : 'Soldé'}
                        </small>
                      </Col>
                    </Row>
                  </Card.Body>
                </Card>

                {singleResult.has_complement && singleResult.complements && singleResult.complements.length > 0 && (
                  <Alert variant="info" className="mb-3">
                    <div className="d-flex align-items-start">
                      <FileText className="me-2 mt-1" size={16} />
                      <div>
                        <strong>Compléments :</strong> {singleResult.complements.length} complément(s)
                        {singleResult.complements.map((comp, idx) => (
                          <div key={idx} className="mt-1">
                            <small>
                              #{idx + 1} - {formatCurrency(comp.amount)} 
                              <Badge bg={comp.status === 'paid' ? 'success' : 'warning'} className="ms-2">
                                {comp.status === 'paid' ? 'Payé' : 'En attente'}
                              </Badge>
                              {comp.collectes_count > 0 && (
                                <span className="text-muted ms-2">({comp.collectes_count} collectes)</span>
                              )}
                            </small>
                          </div>
                        ))}
                      </div>
                    </div>
                  </Alert>
                )}

                <div className="text-center">
                  <small className="text-muted">
                    Prime calculée à 2 $ par collecte effectuée
                  </small>
                </div>
              </>
            )}
          </Modal.Body>
          <Modal.Footer className="border-0">
            <Button variant="secondary" onClick={() => {
              setShowResultModal(false);
              setSingleResult(null);
            }}>
              Fermer
            </Button>
            {singleResult && singleResult.total_pending > 0 && !singleResult.is_fully_paid && (
              <Button
                variant="success"
                onClick={() => {
                  const bonusToPay = {
                    id: singleResult.bonus_id,
                    amount: singleResult.total_pending,
                    remaining_balance: singleResult.total_pending,
                    total_amount: singleResult.total_amount,
                    total_paid: singleResult.total_paid || 0,
                    collectes_count: singleResult.collectes_count || 0,
                    fixed_rate_per_collecte: singleResult.fixed_rate_per_collecte || 2,
                    agent: { name: singleResult.agent_name },
                    user_id: singleResult.agent_id
                  };
                  setSelectedBonus(bonusToPay);
                  setShowResultModal(false);
                  setShowPayModal(true);
                }}
              >
                <Wallet2 className="me-2" />
                Payer {formatCurrency(singleResult.total_pending)} restant
              </Button>
            )}
          </Modal.Footer>
        </Modal>

        <style>{`
          .stats-card { 
            transition: transform 0.3s ease, box-shadow 0.3s ease; 
            border-radius: 12px; 
            overflow: hidden; 
            position: relative;
          }
          .stats-card:hover { 
            transform: translateY(-5px); 
            box-shadow: 0 10px 30px rgba(0,0,0,0.1) !important; 
          }
          .card-gradient { 
            position: absolute; 
            top: 0; 
            left: 0; 
            width: 100%; 
            height: 4px; 
            opacity: 0.8; 
          }
          .form-control:focus, .form-select:focus { 
            border-color: #0d6efd; 
            box-shadow: 0 0 0 0.2rem rgba(13, 110, 253, 0.25); 
          }
          .table > :not(caption) > * > * {
            padding: 0.75rem 0.75rem;
          }
          .btn-sm {
            padding: 0.25rem 0.5rem;
          }
        `}</style>

        <Footer />
      </div>
    </>
  );
};

export default MobilisationBonuses;